import { useEffect, useRef, useState } from "react";
import { BILLING_MODEL_LABELS, MAINTENANCE_MODE_LABELS } from "@shared/roi/defaults";
import { periodLabel } from "@shared/roi/format";
import type { BillingModel, MaintenanceMode, RecurringPeriod } from "@shared/roi/types";
import { Button } from "@client/components/Button";
import { SelectField, TextField } from "@client/components/Field";
import { MoneyField, PercentField, PriceField } from "@client/components/editor/NumberFields";
import { Segmented } from "@client/components/editor/Segmented";
import { ChevronDownIcon } from "@client/components/icons";
import { PACKAGE_COLOR_INDEXES, packageColorVar } from "@client/lib/packageColors";
import type { EditorPackage } from "@client/lib/scenarioDraft";

/**
 * One sellable tier.
 *
 * The card shows **only** the money fields the chosen billing model reads. A
 * perpetual package has no monthly price and a subscription has no maintenance
 * rate, and leaving those on screen greyed out invites people to fill them in
 * and then wonder why the projection ignored them. The values are kept in the
 * model either way, so flipping between models and back loses nothing.
 */

const BILLING_OPTIONS = [
  {
    value: "SUBSCRIPTION" as const,
    label: BILLING_MODEL_LABELS.SUBSCRIPTION,
    hint: "A recurring fee. No licence sale.",
  },
  {
    value: "PERPETUAL" as const,
    label: BILLING_MODEL_LABELS.PERPETUAL,
    hint: "A licence sold once, with optional annual maintenance.",
  },
  {
    value: "HYBRID" as const,
    label: BILLING_MODEL_LABELS.HYBRID,
    hint: "A licence sale and a recurring fee.",
  },
];

interface Props {
  pkg: EditorPackage;
  currencyCode: string;
  index: number;
  count: number;
  disabled: boolean;
  onPatch: (patch: Partial<EditorPackage>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  /** False for the last package: a scenario has to keep one. */
  canRemove: boolean;
}

export function PackageCard({
  pkg,
  currencyCode,
  index,
  count,
  disabled,
  onPatch,
  onMove,
  onRemove,
  canRemove,
}: Props) {
  const showsRecurring = pkg.billingModel !== "PERPETUAL";
  const showsMaintenance = pkg.billingModel !== "SUBSCRIPTION";
  const licenceLabel = pkg.billingModel === "SUBSCRIPTION" ? "Setup fee" : "Licence fee";

  return (
    <li
      // The anchor a warning about this package links to — see `warningTarget`.
      // `scroll-mt` keeps the card clear of the sticky KPI strip on arrival.
      id={`package-${pkg.id}`}
      className="flex scroll-mt-32 flex-col gap-3 rounded-xl border border-line bg-chip-bg p-3"
      // The card's own left edge carries the package colour, so a card and the
      // series it feeds are recognisable without reading either label.
      style={{ borderLeft: `3px solid ${packageColorVar(pkg.colorIndex)}` }}
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-[10rem] flex-1">
          <TextField
            label="Package name"
            value={pkg.name}
            maxLength={60}
            disabled={disabled}
            onChange={(event) => onPatch({ name: event.target.value })}
          />
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="quiet"
            size="sm"
            aria-label={`Move ${pkg.name || "this package"} up`}
            disabled={disabled || index === 0}
            onClick={() => onMove(-1)}
          >
            ↑
          </Button>
          <Button
            variant="quiet"
            size="sm"
            aria-label={`Move ${pkg.name || "this package"} down`}
            disabled={disabled || index === count - 1}
            onClick={() => onMove(1)}
          >
            ↓
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={disabled || !canRemove}
            title={canRemove ? undefined : "A scenario needs at least one package."}
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <Segmented
          legend="Billing model"
          value={pkg.billingModel}
          options={BILLING_OPTIONS}
          disabled={disabled}
          size="sm"
          onChange={(billingModel: BillingModel) => onPatch({ billingModel })}
        />
        <ColorPicker
          name={pkg.name}
          colorIndex={pkg.colorIndex}
          disabled={disabled}
          onPick={(colorIndex) => onPatch({ colorIndex })}
        />
      </div>

      {showsRecurring && (
        <div className="grid gap-3 sm:grid-cols-3">
          <PriceField
            label="Recurring price"
            tone="revenue"
            value={pkg.recurringPrice}
            currencyCode={currencyCode}
            disabled={disabled}
            hint={`Charged ${periodLabel(pkg.recurringPeriod)}, per active client.`}
            onChange={(recurringPrice) => onPatch({ recurringPrice })}
          />
          <MoneyField
            label="Recurring cost"
            tone="cost"
            value={pkg.recurringCost}
            currencyCode={currencyCode}
            disabled={disabled}
            hint={`What serving one client costs ${periodLabel(pkg.recurringPeriod)}.`}
            onChange={(recurringCost) => onPatch({ recurringCost })}
          />
          <SelectField
            label="Billed"
            value={pkg.recurringPeriod}
            disabled={disabled}
            onChange={(event) =>
              onPatch({ recurringPeriod: event.target.value as RecurringPeriod })
            }
          >
            <option value="MONTHLY">Monthly</option>
            <option value="QUARTERLY">Quarterly</option>
            <option value="ANNUAL">Annually</option>
          </SelectField>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <PriceField
          label={licenceLabel}
          tone="revenue"
          value={pkg.oneTimePrice}
          currencyCode={currencyCode}
          disabled={disabled}
          hint={
            pkg.billingModel === "SUBSCRIPTION"
              ? "Charged once, when a client signs."
              : "Booked in full in the year the licence is sold."
          }
          onChange={(oneTimePrice) => onPatch({ oneTimePrice })}
        />
        <MoneyField
          label="Onboarding cost"
          tone="cost"
          value={pkg.oneTimeCost}
          currencyCode={currencyCode}
          disabled={disabled}
          hint="Labour to get one new client running."
          onChange={(oneTimeCost) => onPatch({ oneTimeCost })}
        />
        <MoneyField
          label="Hardware per client"
          tone="cost"
          value={pkg.assetCost}
          currencyCode={currencyCode}
          disabled={disabled}
          hint="Capex at signing. Returned to the pool on churn if the scenario reuses assets."
          onChange={(assetCost) => onPatch({ assetCost })}
        />
      </div>

      {showsMaintenance && (
        <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
          <SelectField
            label="Annual maintenance"
            hint="What the client pays each year after the licence sale to keep support and updates."
            value={pkg.maintenanceMode}
            disabled={disabled}
            onChange={(event) =>
              onPatch({ maintenanceMode: event.target.value as MaintenanceMode })
            }
          >
            {(Object.keys(MAINTENANCE_MODE_LABELS) as MaintenanceMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {MAINTENANCE_MODE_LABELS[mode]}
              </option>
            ))}
          </SelectField>

          {pkg.maintenanceMode !== "NONE" && (
            <div className="grid gap-3 sm:grid-cols-3">
              {pkg.maintenanceMode === "PERCENT_OF_LICENCE" ? (
                <PercentField
                  label="Maintenance rate"
                  tone="revenue"
                  value={pkg.maintenanceRatePct}
                  disabled={disabled}
                  hint="Of the licence fee, every year."
                  onChange={(maintenanceRatePct) => onPatch({ maintenanceRatePct })}
                />
              ) : (
                <MoneyField
                  label="Maintenance amount"
                  tone="revenue"
                  value={pkg.maintenanceFlatAmount}
                  currencyCode={currencyCode}
                  disabled={disabled}
                  hint="Per client, every year."
                  onChange={(maintenanceFlatAmount) => onPatch({ maintenanceFlatAmount })}
                />
              )}

              <SelectField
                label="Starts in"
                hint="Year 1 bundles support into the licence; year 2 charges for it from the year after the sale."
                value={pkg.maintenanceStartYear}
                disabled={disabled}
                onChange={(event) =>
                  onPatch({ maintenanceStartYear: Number(event.target.value) === 1 ? 1 : 2 })
                }
              >
                <option value={1}>Year 1</option>
                <option value={2}>Year 2</option>
              </SelectField>

              <PercentField
                label="Attach rate"
                value={pkg.maintenanceAttachPct}
                disabled={disabled}
                hint="Share of licence holders who actually buy maintenance."
                onChange={(maintenanceAttachPct) => onPatch({ maintenanceAttachPct })}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The package's colour: the chosen one on show, the rest a click away.
 *
 * All six swatches used to sit in a row, which spends the width of six controls
 * saying one thing — and the five that are not chosen are noise on a card that
 * already carries a name, a billing model and up to five money fields. The
 * chosen one stays visible as the trigger while the others are open, so nothing
 * is hidden at the moment of choosing.
 *
 * Plain buttons rather than `role="listbox"`/`role="option"`: a listbox promises
 * arrow-key navigation, and promising it without implementing it is worse for a
 * screen reader than the Tab-through these get for free.
 */
function ColorPicker({
  name,
  colorIndex,
  disabled,
  onPick,
}: {
  name: string;
  colorIndex: number;
  disabled: boolean;
  onPick: (index: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const label = name || "this package";

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      // Stopped so closing the swatches does not also close the dialog the
      // package card is sitting in.
      event.stopPropagation();
      close(true);
    }

    // Capture phase, so a pointerdown on something that stops propagation
    // still dismisses this.
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <span className="mb-1 block text-xs font-semibold text-ink">Colour</span>

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Colour ${colorIndex + 1} for ${label}. Pick another.`}
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1 rounded-lg border border-line bg-chip-bg px-1.5 py-1 text-ink-soft transition hover:border-azure-deep disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          aria-hidden="true"
          className="h-6 w-6 rounded-full"
          style={{ background: packageColorVar(colorIndex) }}
        />
        <ChevronDownIcon className="h-4 w-4 shrink-0" />
      </button>

      {open && (
        <div
          role="group"
          aria-label={`Other colours for ${label}`}
          className="absolute left-0 top-full z-30 mt-1.5 flex gap-1 rounded-lg border border-line bg-surface-strong p-1.5 shadow-lg"
        >
          {PACKAGE_COLOR_INDEXES.filter((slot) => slot !== colorIndex).map((slot) => (
            <button
              key={slot}
              type="button"
              aria-label={`Colour ${slot + 1}`}
              onClick={() => {
                onPick(slot);
                close(true);
              }}
              className="h-6 w-6 rounded-full border-2 border-transparent transition hover:border-ink"
              style={{ background: packageColorVar(slot) }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
